import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { keyframes } from '@emotion/core';

export const MapDrawerOuter = styled.div`
  position: relative;
  user-select: none;
  flex-basis: 100%;

  ${({ theme }) => theme.breakpoints.md.up} {
    flex-grow: 1;
  }

  ${({ isDrawingEnabled }) =>
    isDrawingEnabled &&
    `
      cursor: url('images/scissor.png'), pointer !important;
      .leaflet-interactive {
        cursor: url('images/scissor.png'), pointer !important;
      }
    `}
`;

export const MapDrawerInner = styled.div`
  flex-basis: 100%;
  position: relative;

  .leaflet-container {
    height: 130vw;
    background: white !important;
  }
  ${({ theme }) => theme.breakpoints.sm.up} {
    .leaflet-container {
      height: 500px;
    }
  }
  ${({ theme }) => theme.breakpoints.md.up} {
    .leaflet-container {
      height: 800px;
      height: calc(100vh - 190px);
    }
  }
`;

const moveAlong = keyframes`
0% {
  transform: translateX(-50%);
  opacity: 0;
  visibility: visible;
}
25%, 75% {
  opacity: 1;
}
100% {
  visibility: visible;
  transform: translateX(50%);
  opacity: 0;
}
`;

const cut1 = keyframes`
0% {
  transform: rotate(8deg);
}
50% {
  transform: rotate(-20deg);
}
100% {
  transform: rotate(8deg);
}
`;

const cut2 = keyframes`
0% {
  transform: scale(1, -1) rotate(10deg);
}
50% {
  transform: scale(1, -1) rotate(-20deg);
}
100% {
  transform: scale(1, -1) rotate(10deg);
}
`;

export const Scissor = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 250ms linear;
  pointer-events: none;

  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    opacity: 1;
    transition: opacity 250ms linear;
    transform-origin: 17px 14px;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='44' height='29' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M.779 2.812c3.51-5.88 13.178-1.767 8.89 5.244a4086.002 4086.002 0 0034.005 18.81c-1.075 1.55-2.278 2.058-3.608 1.527-11.903-6.112-22.456-11.37-31.658-15.777-1.746-.881-11.14-3.924-7.63-9.804zm4.504.546a2 2 0 100 4 2 2 0 000-4z' fill='%23${(
      p
    ) =>
      p.theme.colors.scissor.replace(
        /^#/,
        ''
      )}' fill-rule='evenodd'/%3e%3c/svg%3e");
    background-size: contain;
    width: 44px;
    height: 29px;
    ${(p) => p.theme.breakpoints.sm.down} {
      width: 30px;
      height: 20px;
      transform-origin: 12px 10px;
    }
  }
  &:before {
    animation: ${cut1} 500ms ease-in-out infinite;
  }
  &:after {
    animation: ${cut2} 500ms ease-in-out infinite;
  }

  ${({ isDrawing, showDrawEnableOverlay }) =>
    (isDrawing || showDrawEnableOverlay) &&
    `
    &:before, &:after {
      opacity: 0;
    }
  `}
`;

export const Scissor1 = styled(Scissor)`
  top: 20%;
  left: 0;
  animation: ${moveAlong} 5000ms 0ms linear infinite;
`;

export const Scissor2 = styled(Scissor)`
  top: 40%;
  left: 0;
  animation: ${moveAlong} 5000ms 2000ms linear infinite;
`;

export const Scissor3 = styled(Scissor)`
  top: 60%;
  left: 0;
  animation: ${moveAlong} 5000ms 1000ms linear infinite;
`;

export const ScissorContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${(p) =>
    p.isOverlay ? 'rgba(255, 255, 255, 0.5)' : 'none'};
  z-index: 999;
  pointer-events: ${(p) => (p.isOverlay ? 'all' : 'none')};
  overflow: hidden;

  ${({ isOverlay, theme }) =>
    isOverlay &&
    `
    :after {
      content: "Schneiden starten";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: #fff;
      font-size: 1.3rem;
      background-color: ${theme.colors.scissor};
      box-shadow: none;
      border-radius: 3px;
      font-weight: 700;
      padding: 12px 24px;
    }
  `}
`;

const sharedCircleStyles = ({ theme }) => css`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.65);
  font-family: ${theme.fonts.sans};
  font-weight: 500;
  box-sizing: border-box;
  padding: 10px 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0.85;
  text-align: center;
  pointer-events: none;
  font-size: 14px;
  width: 165px;
  height: 165px;

  ${theme.breakpoints.sm.up} {
    font-size: 16px;
    width: 200px;
    height: 200px;
  }
`;

export const BlackCircle = styled.div`
  ${sharedCircleStyles}
  color: white;
  transition: opacity 250ms linear;

  ${({ isDrawing }) =>
    isDrawing &&
    `
    opacity: 0;
  `}
`;

const bounce = keyframes`
  0% {
    transform: translateX(-50%) translateY(-50%) translateX(0px);
    timing-function: ease-in;
    opacity: 0;
  }
  5% {
    opacity: 1;
  }
  6% {
    transform: translateX(-50%) translateY(-50%) translateX(5px);
    timing-function: ease-out;
  }
  9% {
    transform: translateX(-50%) translateY(-50%) translateX(-5px);
    timing-function: ease-in;
  }
  12% {
    transform: translateX(-50%) translateY(-50%) translateX(4px);
    timing-function: ease-out;
  }
  14% {
    transform: translateX(-50%) translateY(-50%) translateX(-4px);
    timing-function: ease-in;
  }
  15% {
    transform: translateX(-50%) translateY(-50%) translateX(2px);
    timing-function: ease-out;
  }
  16% {
    transform: translateX(-50%) translateY(-50%) translateX(-2px);
    timing-function: ease-in;
  }
  17% {
    transform: translateX(-50%) translateY(-50%) translateX(0px);
    timing-function: ease-in;
  }
  95% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

export const ErrorMessage = styled.div`
  ${sharedCircleStyles}
  opacity: 0;
  background-color: rgba(255, 255, 255, 0.75);
  color: ${({ theme }) => theme.colors.red};
  animation-name: ${bounce};
  animation-duration: 4s;
  animation-delay: 0.5s;
`;
